import Table, { Row, TableHeading, TableAction, TableFooter } from "@/components/Table";
import { buildSourceColumn } from "@/components/Table/buildSourceColumn";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import AvatarDetails from "@/components/AvatarDetails";
import IconButton from "@/components/IconButton";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toFormat } from "@/lib/dateHelpers";
import {
  Reference,
  getReferenceTypeLabel,
  getReferenceIcon,
  getReferenceName,
  getReferenceSubtext,
  getReferenceModifiedDate
} from "./types";

type ReferencesTableVariant = "top-level" | "details-panel";

export interface ReferencesTableProps {
  variant?: ReferencesTableVariant;
  references: Reference[];
  loading?: boolean;
  title?: string;
  tableHeading?: string;
  tableBody?: string;
  actions?: TableAction[];
  getParentLink?: (ref: Reference) => string | null | undefined;
  onRemoveReference?: (ref: Reference) => void;
  highlightReferenceId?: string | null;
}

function ReferenceItemCell({ row }: { row: Row<Reference> }) {
  const ref = row.original;
  const icon = getReferenceIcon(ref.__typename);
  const displayName = getReferenceName(ref);
  const url = ref.__typename === "BackofficePersonProfileDocument" && "url" in ref ? ref.url : undefined;

  const title =
    url && displayName ? (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          window.open(url, "_blank", "noopener,noreferrer");
        }}
        className="text-left underline text-sys-brand-primary hover:text-sys-brand-primary-dark cursor-pointer p-0 m-0 bg-transparent border-0"
      >
        {displayName}
      </button>
    ) : (
      displayName || "--"
    );

  return <AvatarDetails icon={icon} title={title} text={getReferenceSubtext(ref)} />;
}

function getNotableType(ref: Reference): string | undefined {
  return "notableType" in ref ? ref.notableType : undefined;
}

function buildReferencesTableColumns(
  getParentLink?: (ref: Reference) => string | null | undefined,
  onRemoveReference?: (ref: Reference) => void,
  navigate?: (to: string) => void,
  currentPathname?: string
): ColumnDef<Reference>[] {
  const columns: ColumnDef<Reference>[] = [
    {
      id: "referenceItem",
      header: "Reference Item",
      size: 350,
      enableSorting: true,
      accessorFn: (ref: Reference) => getReferenceName(ref),
      cell: ReferenceItemCell,
      meta: {
        headerClassName: "hide-header-in-column",
        sortType: "text"
      }
    },
    buildSourceColumn<Reference>({
      accessorKey: "source",
      header: "Source",
      size: 150,
      enableSorting: true,
      sortType: "text" as const,
      accessorFn: (ref: Reference) => getReferenceTypeLabel(ref.__typename, getNotableType(ref)),
      getParentLink: (ref) => getParentLink?.(ref) ?? null,
      getParentType: (ref) => getReferenceTypeLabel(ref.__typename, getNotableType(ref)),
      navigate,
      currentPathname
    }),
    {
      id: "updatedAt",
      header: "Modified",
      size: 150,
      enableSorting: true,
      accessorFn: (ref: Reference) => getReferenceModifiedDate(ref),
      meta: { sortType: "date" },
      cell: ({ row }) => {
        const date = getReferenceModifiedDate(row.original);
        if (!date) {
          return <span className="opacity-20">--</span>;
        }
        const formatted = toFormat(date, "MMM dd, yyyy");
        return formatted || <span className="opacity-20">--</span>;
      }
    }
  ];

  if (onRemoveReference) {
    columns.push({
      id: "actions",
      header: () => null,
      enableSorting: false,
      size: 60,
      cell: ({ row }) => (
        <div className="flex items-center justify-end">
          <IconButton
            name="link_off"
            title="Remove reference"
            className="!m-0 !p-0 h-10"
            data-testid={`remove-reference-${row.original.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onRemoveReference(row.original);
            }}
          />
        </div>
      ),
      meta: { skipEmptyState: true }
    });
  }

  return columns;
}

function getRowUrl(ref: Reference): string | undefined {
  if (ref.__typename === "BackofficePersonProfileDocument" && "url" in ref && ref.url) {
    return ref.url;
  }
  return undefined;
}

export default function ReferencesTable({
  variant = "details-panel",
  references,
  loading = false,
  title = "References",
  tableHeading,
  tableBody,
  actions = [],
  getParentLink,
  onRemoveReference,
  highlightReferenceId
}: ReferencesTableProps) {
  const [sortingState, setSortingState] = useState<SortingState>([{ id: "updatedAt", desc: true }]);
  const hasScrolledToHighlight = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();
  const columns = useMemo(
    () => buildReferencesTableColumns(getParentLink, onRemoveReference, navigate, location.pathname),
    [getParentLink, onRemoveReference, navigate, location.pathname]
  );

  const highlightObserverRef = useCallback(
    (node: HTMLElement | null) => {
      if (!node || !highlightReferenceId || hasScrolledToHighlight.current) return;

      const scrollToHighlight = (container: HTMLElement) => {
        const row = container.querySelector("tr.row-highlight") as HTMLElement | null;
        if (row) {
          hasScrolledToHighlight.current = true;
          row.scrollIntoView({ behavior: "smooth", block: "center" });
          return true;
        }
        return false;
      };

      if (scrollToHighlight(node)) return;

      const observer = new MutationObserver(() => {
        if (scrollToHighlight(node)) observer.disconnect();
      });
      observer.observe(node, { childList: true, subtree: true });

      return () => observer.disconnect();
    },
    [highlightReferenceId]
  );

  const handleRowClick = (row: Row<Reference>) => {
    const url = getRowUrl(row.original);
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    const parentLink = getParentLink?.(row.original);
    if (parentLink) {
      navigate(parentLink);
    }
  };

  const getRowClasses = highlightReferenceId
    ? (row: Row<Reference>) => (row.original.id === highlightReferenceId ? "row-highlight" : "")
    : undefined;

  if (variant === "details-panel") {
    return (
      <TableDetailsPanel
        data={references}
        columns={columns}
        title={title}
        loading={loading}
        onClickRow={handleRowClick}
        sortingState={sortingState}
        onSortingChange={setSortingState}
      />
    );
  }

  return (
    <div ref={highlightReferenceId ? highlightObserverRef : undefined}>
      {(tableHeading || tableBody || actions.length > 0) && (
        <TableHeading heading={tableHeading} body={tableBody} actions={actions} />
      )}
      <Table
        data={references}
        columns={columns}
        loading={loading}
        onClickRow={handleRowClick}
        sortingState={sortingState}
        onSortingChange={setSortingState}
        renderFooter={() => <TableFooter totalRowCount={references.length} />}
        data-testid="references-table"
        getRowClasses={getRowClasses}
      />
    </div>
  );
}
